import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faPlus, faEdit, faTrash, faSave, faTimes, faSpinner,
  faCreditCard, faUser, faGraduationCap, faCalendar, faDollarSign,
  faSearch, faFilter, faCheckCircle, faTimesCircle, faClock
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

interface Subscription {
  id: number;
  user_id: number;
  exam_id: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  amount_paid: number;
  currency: string;
  payment_id: string;
  created_at: string;
  email: string;
  full_name: string;
  exam_name: string;
}

interface User {
  id: number;
  email: string;
  full_name: string;
}

interface Exam {
  id: number;
  name: string;
}

const AdminSubscriptions: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [examFilter, setExamFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    user_id: '',
    exam_id: '',
    days: '365',
    start_date: new Date().toISOString().split('T')[0]
  });

  const [editData, setEditData] = useState({
    days: '',
    end_date: ''
  });

  useEffect(() => {
    fetchData();
    fetchUsers();
    fetchExams();
  }, [page, search, statusFilter, examFilter]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(examFilter && { examId: examFilter })
      });

      const response = await api.get(`/subscriptions/admin/subscriptions?${params}`);
      setSubscriptions(response.data.subscriptions || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      alert('Failed to load subscriptions. You may not have admin access.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/subscriptions/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await api.get('/exams');
      setExams(response.data.exams || []);
    } catch (error) {
      console.error('Failed to fetch exams:', error);
    }
  };

  const handleCreate = async () => {
    if (!formData.user_id || !formData.exam_id || !formData.days) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await api.post('/subscriptions/admin/subscriptions', {
        user_id: parseInt(formData.user_id),
        exam_id: parseInt(formData.exam_id),
        days: parseInt(formData.days),
        start_date: formData.start_date
      });

      await fetchData();
      setShowCreateForm(false);
      setFormData({
        user_id: '',
        exam_id: '',
        days: '365',
        start_date: new Date().toISOString().split('T')[0]
      });
    } catch (error: any) {
      console.error('Failed to create subscription:', error);
      alert(error.response?.data?.error || 'Failed to create subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleExtend = async (subscriptionId: number) => {
    const days = window.prompt('Enter number of days to extend:');
    if (!days || isNaN(parseInt(days))) {
      return;
    }

    setSaving(true);
    try {
      await api.put(`/subscriptions/admin/subscriptions/${subscriptionId}`, {
        action: 'extend',
        days: parseInt(days)
      });

      await fetchData();
      setEditingId(null);
    } catch (error: any) {
      console.error('Failed to extend subscription:', error);
      alert(error.response?.data?.error || 'Failed to extend subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleEnd = async (subscriptionId: number) => {
    if (!window.confirm('Are you sure you want to end this subscription?')) {
      return;
    }

    setSaving(true);
    try {
      await api.put(`/subscriptions/admin/subscriptions/${subscriptionId}`, {
        action: 'end'
      });

      await fetchData();
      setEditingId(null);
    } catch (error: any) {
      console.error('Failed to end subscription:', error);
      alert(error.response?.data?.error || 'Failed to end subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (subscriptionId: number) => {
    if (!window.confirm('Are you sure you want to delete this subscription? This action cannot be undone.')) {
      return;
    }

    setDeleting(subscriptionId);
    try {
      await api.delete(`/subscriptions/admin/subscriptions/${subscriptionId}`);
      await fetchData();
    } catch (error: any) {
      console.error('Failed to delete subscription:', error);
      alert(error.response?.data?.error || 'Failed to delete subscription');
    } finally {
      setDeleting(null);
    }
  };

  const isSubscriptionActive = (subscription: Subscription) => {
    const now = new Date();
    const startDate = new Date(subscription.start_date);
    const endDate = new Date(subscription.end_date);
    return subscription.is_active && now >= startDate && now <= endDate;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    if (amount === 0) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div className="text-center">
          <div className="gp-spinner mb-3" aria-hidden="true" />
          <p className="text-sm gp-text-muted">Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Manage Subscriptions - Admin - GlobalPreps"
        description="Admin panel for managing user subscriptions"
        url="/admin/subscriptions"
      />
      <div className="gp-page py-6">
        <div className="gp-container">
          <div className="gp-page-header">
            <Link to="/dashboard" className="gp-back-link mb-3">
              <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
              {t('common.backToDashboard')}
            </Link>
            
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="gp-icon-tile" style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }}>
                    <FontAwesomeIcon icon={faCreditCard} aria-hidden="true" />
                  </span>
                  <h1 className="gp-page-title">Subscription Management</h1>
                </div>
                <p className="gp-page-subtitle">
                  Manage user subscriptions and create complementary access
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="gp-btn-primary"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" aria-hidden="true" />
                <span>Create Subscription</span>
              </button>
            </div>
          </div>

          <div className="gp-card mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--gp-ink)' }}>
                  Search Users
                </label>
                <div className="relative">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-xs gp-text-muted" aria-hidden="true" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search by email or name..."
                    className="gp-search-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--gp-ink)' }}>
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="gp-select w-full"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="upcoming">Upcoming</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--gp-ink)' }}>
                  Exam
                </label>
                <select
                  value={examFilter}
                  onChange={(e) => {
                    setExamFilter(e.target.value);
                    setPage(1);
                  }}
                  className="gp-select w-full"
                >
                  <option value="">All Exams</option>
                  {exams.map(exam => (
                    <option key={exam.id} value={exam.id.toString()}>{exam.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {showCreateForm && (
            <div className="gp-card mb-4" style={{ borderColor: 'var(--gp-success)', borderWidth: 2 }}>
              <div
                className="flex items-center justify-between mb-3 pb-3"
                style={{ borderBottom: '1px solid var(--gp-border)' }}
              >
                <h2 className="text-base font-bold" style={{ color: 'var(--gp-ink)' }}>
                  Create Complementary Subscription
                </h2>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="gp-text-muted hover:opacity-80"
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--gp-ink)' }}>
                    User *
                  </label>
                  <select
                    value={formData.user_id}
                    onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                    className="gp-select w-full"
                  >
                    <option value="">Select User</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id.toString()}>
                        {u.email} {u.full_name ? `(${u.full_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--gp-ink)' }}>
                    Exam *
                  </label>
                  <select
                    value={formData.exam_id}
                    onChange={(e) => setFormData({ ...formData, exam_id: e.target.value })}
                    className="gp-select w-full"
                  >
                    <option value="">Select Exam</option>
                    {exams.map(exam => (
                      <option key={exam.id} value={exam.id.toString()}>{exam.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--gp-ink)' }}>
                    Duration (Days) *
                  </label>
                  <input
                    type="number"
                    value={formData.days}
                    onChange={(e) => setFormData({ ...formData, days: e.target.value })}
                    className="input-field"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--gp-ink)' }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="input-field"
                  />
                </div>
              </div>

              <div
                className="flex items-center justify-end gap-2 mt-4 pt-3"
                style={{ borderTop: '1px solid var(--gp-border)' }}
              >
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="gp-btn-ghost"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={saving}
                  className="gp-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" aria-hidden="true" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="text-xs" aria-hidden="true" />
                      <span>Create Subscription</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="gp-card p-0 overflow-hidden">
            {subscriptions.length === 0 ? (
              <div className="text-center py-10 px-4">
                <FontAwesomeIcon icon={faCreditCard} className="text-3xl gp-text-muted mb-3" aria-hidden="true" />
                <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>No Subscriptions Found</h3>
                <p className="text-sm gp-text-muted">
                  {search || statusFilter || examFilter ? 'Try adjusting your filters' : 'Get started by creating a subscription'}
                </p>
              </div>
            ) : (
              <>
                <div className="gp-table-wrap border-0 rounded-none">
                  <table>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Exam</th>
                        <th>Status</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Amount</th>
                        <th className="!text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((subscription) => {
                        const isActive = isSubscriptionActive(subscription);
                        return (
                          <tr key={subscription.id}>
                            <td>
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faUser} className="text-xs gp-text-muted" aria-hidden="true" />
                                <div>
                                  <div className="text-sm font-medium" style={{ color: 'var(--gp-ink)' }}>
                                    {subscription.full_name || subscription.email}
                                  </div>
                                  <div className="text-xs gp-text-muted">
                                    {subscription.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faGraduationCap} className="text-xs" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                                <span className="text-sm" style={{ color: 'var(--gp-ink)' }}>{subscription.exam_name}</span>
                              </div>
                            </td>
                            <td>
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
                                style={
                                  isActive
                                    ? { background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }
                                    : { background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }
                                }
                              >
                                <FontAwesomeIcon icon={isActive ? faCheckCircle : faTimesCircle} className="text-[10px]" aria-hidden="true" />
                                {isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--gp-ink)' }}>
                                <FontAwesomeIcon icon={faCalendar} className="text-xs gp-text-muted" aria-hidden="true" />
                                <span>{formatDate(subscription.start_date)}</span>
                              </div>
                            </td>
                            <td>
                              <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--gp-ink)' }}>
                                <FontAwesomeIcon icon={faCalendar} className="text-xs gp-text-muted" aria-hidden="true" />
                                <span>{formatDate(subscription.end_date)}</span>
                              </div>
                            </td>
                            <td>
                              <div className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--gp-ink)' }}>
                                <FontAwesomeIcon icon={faDollarSign} className="text-xs gp-text-muted" aria-hidden="true" />
                                <span>{formatCurrency(subscription.amount_paid, subscription.currency)}</span>
                              </div>
                            </td>
                            <td className="!text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                {isActive && (
                                  <button
                                    type="button"
                                    onClick={() => handleExtend(subscription.id)}
                                    className="p-1.5 rounded text-xs font-medium transition-colors"
                                    style={{ background: 'var(--gp-brand)', color: '#fff' }}
                                    title="Extend Subscription"
                                  >
                                    <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                                  </button>
                                )}
                                {isActive && (
                                  <button
                                    type="button"
                                    onClick={() => handleEnd(subscription.id)}
                                    className="p-1.5 rounded text-xs font-medium transition-colors"
                                    style={{ background: 'var(--gp-accent)', color: '#fff' }}
                                    title="End Subscription"
                                  >
                                    <FontAwesomeIcon icon={faTimesCircle} aria-hidden="true" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleDelete(subscription.id)}
                                  disabled={deleting === subscription.id}
                                  className="p-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  style={{ background: '#b91c1c', color: '#fff' }}
                                  title="Delete Subscription"
                                >
                                  {deleting === subscription.id ? (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" aria-hidden="true" />
                                  ) : (
                                    <FontAwesomeIcon icon={faTrash} aria-hidden="true" />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div
                    className="flex items-center justify-between px-4 py-3"
                    style={{ borderTop: '1px solid var(--gp-border)' }}
                  >
                    <div className="text-sm gp-text-muted">
                      Page {page} of {totalPages}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="gp-btn-ghost py-1.5 px-3 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="gp-btn-ghost py-1.5 px-3 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSubscriptions;
